'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import { WEBSITE_PAGE_BACKGROUND_DARK } from '@/consts/website-theme';
import { productManualSlugPath } from '@/consts/routes';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';
import NewsMediaPageHeader from './NewsMediaPageHeader';

const PRODUCT_MANUALS_INTRO =
  'Installation guides and product documentation for NIICO cabinetry and millwork.';

function ProductManualCard({ manual }) {
  const cardImage = manual.cardImage ?? manual.card_image;
  const cardImageAlt =
    manual.cardImageAlt ?? manual.card_image_alt ?? `${manual.title} product manual`;
  const href = productManualSlugPath(manual.slug);

  return (
    <Box
      component={Link}
      href={href}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover .product-manual-card-media': {
          opacity: 0.88,
        },
      }}
    >
      {cardImage ? (
        <Box
          className="product-manual-card-media"
          component="img"
          src={cardImage}
          alt={cardImageAlt}
          sx={{
            width: '100%',
            height: { xs: 240, sm: 280, md: 300 },
            objectFit: 'cover',
            display: 'block',
            transition: 'opacity 0.3s ease',
          }}
        />
      ) : (
        <Box
          className="product-manual-card-media"
          sx={{
            width: '100%',
            height: { xs: 240, sm: 280, md: 300 },
            bgcolor: WEBSITE_PAGE_BACKGROUND_DARK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            transition: 'opacity 0.3s ease',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              letterSpacing: '0.14em',
              color: 'rgba(0, 0, 0, 0.45)',
              textAlign: 'center',
            }}
          >
            {manual.title}
          </Typography>
        </Box>
      )}
      <Box sx={{ pt: 1.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {manual.title}
        </Typography>
        {manual.intro ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {manual.intro}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

ProductManualCard.propTypes = {
  manual: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    intro: PropTypes.string,
    cardImage: PropTypes.string,
    card_image: PropTypes.string,
    cardImageAlt: PropTypes.string,
    card_image_alt: PropTypes.string,
  }).isRequired,
};

export default function ProductManualsPage({ manuals }) {
  return (
    <WebsiteInteriorScrollLayout>
      <NewsMediaPageHeader title="Product Manuals" introCopy={PRODUCT_MANUALS_INTRO} />
      <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {manuals.length === 0 ? (
            <Typography color="text.secondary">No product manuals available yet.</Typography>
          ) : (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {manuals.map(manual => (
                <Grid key={manual.slug} item xs={12} sm={6} md={4}>
                  <ProductManualCard manual={manual} />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

ProductManualsPage.propTypes = {
  manuals: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      intro: PropTypes.string,
      cardImage: PropTypes.string,
      card_image: PropTypes.string,
      cardImageAlt: PropTypes.string,
      card_image_alt: PropTypes.string,
    }),
  ).isRequired,
};
